// ─────────────────────────────────────────────────────────────────────────────
//  graphs/BarChart.jsx (ColumnChart)
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import ReactApexChart from "react-apexcharts";
import {
    ChartAnimationWrapper, ErrorMessage,
    getBaseChartConfig, getSeriesColors, getSingleUnit,
    formatNumberWithSuffix, formatYAxisSmart, sanitiseSeriesName,
    formatSeries, detectUnitField,
    BIOMEDICAL_WASTE_COLORS, DEFAULT_COLORS,
    axisLabelStyle, monoLabelStyle,
} from "../mainUnit";

// ─── TRANSFORM ────────────────────────────────────────────────────────────────
const transformData = (rawData, groupedField, title, dataSourceType) => {
    if (!rawData || rawData.length === 0) return null;

    const xAxisField   = groupedField?.[0];
    const stackByField = groupedField?.[1] || null;
    let series = [], categories = [], colorMap = {}, unitsMap = {}, showCategoryLegend = true;

    if (stackByField && xAxisField) {
        const groupedData = {}, stackValues = new Set();
        rawData.forEach(item => {
            const xVal     = item[xAxisField];
            const stackVal = item[stackByField];
            const value    = parseFloat(item.sum_value || item.min_value) || 0;
            stackValues.add(stackVal);
            if (!groupedData[stackVal]) groupedData[stackVal] = {};
            groupedData[stackVal][xVal] = (groupedData[stackVal][xVal] || 0) + value;
        });

        const totals = {};
        rawData.forEach(item => {
            const xVal  = item[xAxisField];
            const value = parseFloat(item.sum_value || item.min_value) || 0;
            totals[xVal] = (totals[xVal] || 0) + value;
        });
        categories = Object.keys(totals);

        const { unitField: detectedUnitField, unitsMap: detectedUnitsMap } = detectUnitField(rawData, xAxisField, stackByField);
        unitsMap = detectedUnitsMap;
        const unitOnStackBy = detectedUnitField === stackByField;

        const stackByUnitLookup = {};
        rawData.forEach(item => {
            if (!item.unit || !stackByField) return;
            stackByUnitLookup[String(item[stackByField] ?? '')] = item.unit;
        });

        [...stackValues].forEach((stackVal, index) => {
            const color = BIOMEDICAL_WASTE_COLORS[stackVal] || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
            colorMap[stackVal] = color;
            const unit = unitOnStackBy ? (stackByUnitLookup[stackVal] ?? null) : null;
            series.push({ name: String(stackVal), unit, data: categories.map(xVal => groupedData[stackVal][xVal] || 0), color });
        });
    } else if (xAxisField) {
        showCategoryLegend = true;
        const aggregated = {};
        rawData.forEach(item => {
            const label = item[xAxisField] || 'Unknown';
            aggregated[label] = (aggregated[label] || 0) + (parseFloat(item.sum_value || item.min_value) || 0);
            if (item.unit) unitsMap[label] = item.unit;
        });
        const labels = Object.keys(aggregated);
        labels.forEach((lbl, idx) => { colorMap[lbl] = BIOMEDICAL_WASTE_COLORS[lbl] || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]; });
        series = [{ data: labels.map(lbl => ({ x: lbl, y: aggregated[lbl], fillColor: colorMap[lbl] })) }];
    }

    return {
        graphType: 'column', dataSourceType, title: title || 'Analysis',
        categories, series: formatSeries(series),
        colors: DEFAULT_COLORS, colorMap, unitsMap,
        stacked: !!stackByField, showCategoryLegend,
    };
};

// ─── TOOLTIP: distributed single-series (one colour per bar) ─────────────────
const buildDistributedTooltip = (barColors, sharedUnit, unitsMap) => {
    return ({ series, seriesIndex, dataPointIndex, w }) => {
        const dp     = w.config.series[0]?.data?.[dataPointIndex];
        const label  = dp?.x ?? `Point ${dataPointIndex + 1}`;
        const val    = series[seriesIndex]?.[dataPointIndex] ?? dp?.y ?? 0;
        const color  = dp?.fillColor ?? barColors[dataPointIndex % barColors.length];
        const unit   = sharedUnit || unitsMap?.[label] || '';
        const numVal = typeof val === 'number' ? val : (parseFloat(val) || 0);
        return `
            <div style="padding:10px 14px;background:#fff;border:1px solid #e2e8f0;border-radius:10px;
                box-shadow:0 4px 16px rgba(15,23,42,0.1);font-family:'DM Sans',system-ui,sans-serif;
                min-width:160px;max-width:280px;">
                <div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:8px;">
                    <div style="width:10px;height:10px;border-radius:50%;background:${color};flex-shrink:0;margin-top:3px;"></div>
                    <span style="font-size:12.5px;font-weight:700;color:#0f172a;line-height:1.4;word-break:break-word;">${label}</span>
                </div>
                <div style="font-size:20px;font-weight:900;color:#0f172a;font-family:'DM Mono',monospace;letter-spacing:-0.02em;line-height:1;">
                    ${formatNumberWithSuffix(numVal)}
                    ${unit ? `<span style="font-size:11px;font-weight:500;color:#94a3b8;margin-left:5px;">${unit}</span>` : ''}
                </div>
            </div>`;
    };
};

const buildStackedTooltip = (categoriesArr, seriesColors, seriesArr, sharedUnit, unitsMap) => {
    const seriesUnitMap = {};
    (seriesArr || []).forEach(s => {
        if (s.unit) {
            seriesUnitMap[String(s.name)]                     = s.unit;
            seriesUnitMap[sanitiseSeriesName(String(s.name))] = s.unit;
        }
    });

    return ({ series, dataPointIndex, w }) => {
        const categoryLabel = (categoriesArr || [])[dataPointIndex] ?? `#${dataPointIndex}`;
        const categoryUnit  = unitsMap?.[categoryLabel] || '';

        const rows = (w.config.series || []).map((s, si) => {
            const val   = series[si]?.[dataPointIndex];
            const color = (w.config.colors || seriesColors)[si] || '#94a3b8';
            const name  = sanitiseSeriesName(s.name);
            if (val === null || val === undefined || val === 0) return '';
            const rowUnit = categoryUnit || seriesUnitMap[s.name] || seriesUnitMap[name] || '';
            const labelSuffix = (!categoryUnit && rowUnit)
                ? ` <span style="font-size:10px;color:#94a3b8;">(${rowUnit})</span>`
                : '';
            return `
                <div style="display:flex;align-items:center;gap:8px;padding:2px 0;">
                    <div style="width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0;"></div>
                    <span style="font-size:12px;color:#475569;flex:1;">${name}${labelSuffix}:</span>
                    <span style="font-size:12px;font-weight:700;color:#0f172a;font-family:'DM Mono',monospace;margin-left:8px;">
                        ${formatNumberWithSuffix(val)}
                        ${rowUnit ? `<span style="font-size:10px;font-weight:500;color:#94a3b8;margin-left:3px;">${rowUnit}</span>` : ''}
                    </span>
                </div>`;
        }).filter(Boolean).join('');

        const headerUnit = sharedUnit || categoryUnit || (() => {
            const allSeriesUnits = [...new Set(
                (w.config.series || []).map(s => {
                    const n = sanitiseSeriesName(s.name);
                    return seriesUnitMap[s.name] || seriesUnitMap[n] || '';
                }).filter(Boolean)
            )];
            return allSeriesUnits.length === 1 ? allSeriesUnits[0] : '';
        })();

        return `
            <div style="padding:10px 14px;background:#fff;border:1px solid #e2e8f0;border-radius:10px;
                box-shadow:0 4px 16px rgba(15,23,42,0.1);font-family:'DM Sans',system-ui,sans-serif;
                min-width:180px;max-width:300px;">
                <div style="font-size:12.5px;font-weight:700;color:#0f172a;margin-bottom:8px;
                    padding-bottom:6px;border-bottom:1px solid #f1f5f9;">
                    ${categoryLabel}
                    ${headerUnit ? `<span style="font-size:10px;font-weight:500;color:#94a3b8;margin-left:5px;">(${headerUnit})</span>` : ''}
                </div>
                ${rows || '<div style="font-size:11px;color:#94a3b8;">No data</div>'}
            </div>`;
    };
};

// ─── LEGEND FORMATTER ─────────────────────────────────────────────────────────
const buildLegendFormatter = (seriesArr, sharedUnit) => (seriesName) => {
    const clean = sanitiseSeriesName(seriesName);
    if (sharedUnit) return clean;
    const s = (seriesArr || []).find(x => x.name === seriesName);
    return s?.unit ? `${clean} (${s.unit})` : clean;
};

// ─── AUTO-FIT columnWidth ──────────────────────────────────────────────────────
const calcColumnWidth = (catCount, seriesCount, isStacked, override) => {
    if (override) return override;
    const slots = catCount * (isStacked ? 1 : seriesCount);
    const pct   = Math.min(85, Math.max(10, Math.round(70 - slots * 0.8)));
    return `${pct}%`;
};

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export const BarChart = ({ chartData, chartOptions = {}, groupedField = [] }) => {
    const data = chartOptions.transform
        ? transformData(chartData?.data, groupedField, chartData?.title, chartData?.dataSourceType)
        : chartData;

    if (data?.errorMessage) return <ErrorMessage message={data.errorMessage} />;
    if (!data?.series?.length) return <ErrorMessage />;

    const toolbarVisible     = chartOptions.toolbarVisible !== false;
    const isStacked          = !!data.stacked;
    const seriesColors       = chartOptions.colors || getSeriesColors(data.series, data.colorMap, data.colors);
    const showCategoryLegend = data.showCategoryLegend && !isStacked;
    const barColors          = showCategoryLegend ? Object.values(data.colorMap) : seriesColors;
    const sharedUnit         = getSingleUnit(data.unitsMap);
    const baseConfig         = getBaseChartConfig('light', barColors, data, toolbarVisible);

    const catCount    = data.categories?.length || 1;
    const seriesCount = isStacked ? 1 : (data.series?.length || 1);

    const labelRotate       = catCount > 8  ? -45 : 0;
    const labelRotateAlways = catCount > 16;

    const hasSeriesUnits   = data.series.some(s => !!s.unit);
    const hasCategoryUnits = !sharedUnit && data.unitsMap && Object.keys(data.unitsMap).length > 0;
    const hasMixedUnits    = !sharedUnit && (hasSeriesUnits || hasCategoryUnits);

    const customTooltip = showCategoryLegend
        ? buildDistributedTooltip(barColors, sharedUnit, data.unitsMap)
        : hasMixedUnits
            ? buildStackedTooltip(data.categories, seriesColors, data.series, sharedUnit, data.unitsMap)
            : undefined;

    const legendFormatter = showCategoryLegend
        ? (seriesName) => sanitiseSeriesName(seriesName)
        : buildLegendFormatter(data.series, sharedUnit);

    const chartConfig = {
        ...baseConfig,
        chart: { ...baseConfig.chart, type: 'bar', height: chartOptions.height || 450, stacked: false },
        colors: barColors,
        xaxis: {
            categories: data.categories || [],
            labels: {
                style: axisLabelStyle,
                rotate: labelRotate,
                rotateAlways: labelRotateAlways,
                hideOverlappingLabels: true,
                trim: true,
                maxHeight: catCount > 8 ? 90 : 60,
            },
            // ★ FIX: show x-axis border and ticks
            axisBorder: { show: true,  color: '#94a3b8', height: 1 },
            axisTicks:  { show: true, color: '#94a3b8', height: 6 },
            tickPlacement: 'on',
        },
        yaxis: {
        
            labels: { formatter: formatYAxisSmart, style: monoLabelStyle },
            // ★ FIX: show y-axis border line
            axisBorder: { show: true, color: '#94a3b8', width: 1 ,  offsetX: 0},
            axisTicks:  { show: true, color: '#94a3b8', width: 6,  offsetX: 0 },
        },
        plotOptions: {
            bar: {
                borderRadius: isStacked ? 0 : 5,
                horizontal: false,
                columnWidth: calcColumnWidth(catCount, seriesCount, isStacked, chartOptions.columnWidth),
                borderRadiusApplication: 'end',
                borderRadiusWhenStacked: 'last',
                distributed: showCategoryLegend,
                dataLabels: { position: 'top' },
            },
        },
        dataLabels: {
            enabled: !isStacked && catCount <= 20,
            formatter: (val) => formatNumberWithSuffix(val),
            style: { fontSize: '11px', fontWeight: 700, fontFamily: "'DM Mono', monospace", colors: ['#475569'] },
            offsetY: -22,
        },
        states: {
            hover:  { filter: { type: 'darken', value: 0.88 } },
            active: { filter: { type: 'darken', value: 0.80 } },
        },
        tooltip: { ...baseConfig.tooltip, ...(customTooltip ? { custom: customTooltip } : {}) },
        legend: showCategoryLegend
            ? {
                ...baseConfig.legend,
                customLegendItems: data.categories?.length ? data.categories : Object.keys(data.colorMap || {}),
                markers: { fillColors: data.categories?.length ? barColors : Object.values(data.colorMap || {}) },
                formatter: legendFormatter,
            }
            : { ...baseConfig.legend, formatter: legendFormatter },
    };

    return (
        <ChartAnimationWrapper variant="fadeUp">
            <div style={{ width: '100%' }}>
                <ReactApexChart
                    options={chartConfig}
                    series={data.series}
                    type="bar"
                    height={chartOptions.height || 400}
                />
            </div>
        </ChartAnimationWrapper>
    );
};